import { ParsedUrlQuery } from 'querystring';

/**
 * URL 파라미터에서 UTM 관련 파라미터만 추출
 * @param params 파라미터 객체
 * @returns UTM 파라미터 객체
 */
export const extractUtmParams = (params?: ParsedUrlQuery): Record<string, string> => {
  if (!params) {
    return {};
  }

  return Object.entries(params)
    .filter(([key]) => key.startsWith('utm_'))
    .reduce(
      (acc, [key, value]) => {
        acc[key] = value?.toString() || '';
        return acc;
      },
      {} as Record<string, string>,
    );
};

/**
 * 모든 utm_* 파라미터를 URL에 설정하는 유틸리티 함수
 * @param url 대상 URL
 * @param utmParams utm 파라미터가 포함된 객체
 * @returns 업데이트된 URL
 */
export const applyUtmParamsToUrl = (url: URL, utmParams?: Record<string, string | string[] | undefined>): URL => {
  if (!utmParams) {
    return url;
  }

  Object.entries(utmParams).forEach(([key, value]) => {
    if (key.startsWith('utm_') && value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  });

  return url;
};

/**
 * 모든 utm_* 파라미터를 여러 URL에 설정하는 유틸리티 함수
 * @param urls 대상 URL 객체들
 * @param utmParams utm 파라미터가 포함된 객체
 */
export const applyUtmParamsToUrls = (urls: URL[], utmParams?: Record<string, string | string[] | undefined>): void => {
  if (!utmParams) {
    return;
  }

  urls.forEach((url) => {
    applyUtmParamsToUrl(url, utmParams);
  });
};

import {
  getUtmSourceType,
  NON_PAID_SOURCES_SET,
  PAID_SOURCES_SET,
  UTM_COOKIE_EXCLUDED_PATHS,
  UTM_SOURCE_EXPIRATION_HOURS,
  type UtmSourceType,
} from './constants';

/**
 * UTM 소스에 따른 만료 시간을 반환하는 유틸리티 함수
 * @param utm_source utm_source 값
 * @returns 만료 시간 (시간 단위)
 */
export const getExpirationHoursByUtmSource = (utm_source?: string | string[]): number => {
  if (!utm_source) {
    return UTM_SOURCE_EXPIRATION_HOURS.default;
  }

  const sourceValue = Array.isArray(utm_source) ? utm_source[0] : utm_source;
  return UTM_SOURCE_EXPIRATION_HOURS[sourceValue] || UTM_SOURCE_EXPIRATION_HOURS.default;
};

/**
 * 현재 경로가 UTM 쿠키 설정 제외 대상인지 확인
 * @returns 제외 대상 여부
 */
export const isExcludedPath = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const currentPath = window.location.pathname;
  return UTM_COOKIE_EXCLUDED_PATHS.some((path) => currentPath.startsWith(path));
};

/**
 * UTM 소스가 paid 소스인지 확인
 * @param utmSource utm_source 값
 * @returns paid 소스 여부
 */
export const isPaidSource = (utmSource?: string | string[]): boolean => {
  if (!utmSource) {
    return false;
  }

  const sourceValue = Array.isArray(utmSource) ? utmSource[0] : utmSource;
  return PAID_SOURCES_SET.has(sourceValue);
};

/**
 * UTM 소스가 non-paid 소스인지 확인
 * @param utmSource utm_source 값
 * @returns non-paid 소스 여부
 */
export const isNonPaidSource = (utmSource?: string | string[]): boolean => {
  if (!utmSource) {
    return false;
  }

  const sourceValue = Array.isArray(utmSource) ? utmSource[0] : utmSource;
  return NON_PAID_SOURCES_SET.has(sourceValue);
};

/**
 * UTM 소스의 타입을 확인 (paid/non-paid/unknown)
 * @param utmSource utm_source 값
 * @returns 소스 타입
 */
export const getSourceType = (utmSource?: string | string[]): UtmSourceType => {
  if (!utmSource) {
    return 'unknown';
  }

  const sourceValue = Array.isArray(utmSource) ? utmSource[0] : utmSource;
  return getUtmSourceType(sourceValue);
};

/**
 * UTM 파라미터가 paid 트래픽인지 확인
 * @param utmParams UTM 파라미터 객체
 * @returns paid 트래픽 여부
 */
export const isPaidTraffic = (utmParams: ParsedUrlQuery): boolean => {
  // 1순위: 명시된 paid 여부 우선
  const abChannelType = utmParams.ab_channel_type;
  if (abChannelType === 'paid') {
    return true;
  }

  // 2순위: paid source 목록과 매칭
  if (isPaidSource(utmParams.utm_source)) {
    return true;
  }

  // 3순위: 오가닉으로 간주
  return false;
};

/**
 * UTM 파라미터가 non-paid 트래픽인지 확인
 * @param utmParams UTM 파라미터 객체
 * @returns non-paid 트래픽 여부
 */
export const isNonPaidTraffic = (utmParams: ParsedUrlQuery): boolean => {
  // 1순위: 명시된 non-paid 여부 확인
  const abChannelType = utmParams.ab_channel_type;
  if (abChannelType === 'non-paid') {
    return true;
  }

  // 2순위: non-paid source 목록과 매칭
  if (isNonPaidSource(utmParams.utm_source)) {
    return true;
  }

  // 3순위: paid가 아니면서 utm_source가 있으면 non-paid로 간주
  if (utmParams.utm_source && !isPaidSource(utmParams.utm_source)) {
    return true;
  }

  return false;
};
